import { useInfiniteQuery } from '@tanstack/react-query';

import { getProductsFromPaginatedResponse } from '../product/helpers/getProductsFromPaginatedResponse';

import { type PresApiSearchProduct } from './aliases';
import { fetchSearchProducts } from './api';
import { SEARCH_RESULTS_STALE_TIME } from './constants';
import { getOrCreateNewSearchId } from './getOrCreateNewSearchId';
import { buildSearchQueryParams } from './helpers';
import { buildProductSearchQueryKey } from './queryKeys';
import { useProductsSearch } from './useProductsSearch';
import { useSearchFilters } from './useSearchFilters';
import { useSearchInput } from './useSearchInput';

import { useCurrentLocation } from '@/modules/location/useCurrentLocation';
import { useSessionId } from '@/modules/storage/useSessionId';
import { useCurrentUser } from '@/modules/user/useCurrentUser';

const DEFAULT_LOCATION = process.env.NEXT_PUBLIC_DEFAULT_LOCATION;

export function useProductsSearchWithFallback(query: string = '') {
  const { searchFilters } = useSearchFilters();
  const { searchInput = query } = useSearchInput();
  const { location } = useCurrentLocation();
  const [, user] = useCurrentUser();
  const sessionId = useSessionId();
  const searchResponse = useProductsSearch();
  const searchId = getOrCreateNewSearchId();

  const initialProductsResponse = getProductsFromPaginatedResponse(
    searchResponse.data
  );

  const isInitialResponseEmpty =
    !initialProductsResponse || initialProductsResponse.length === 0;
  const useFallbackQuery =
    isInitialResponseEmpty &&
    !searchResponse.isLoading &&
    location !== DEFAULT_LOCATION;

  const fallbackSearchResponse = useInfiniteQuery({
    queryKey: buildProductSearchQueryKey({
      location: DEFAULT_LOCATION,
      searchInput,
      searchFilters,
    }),
    queryFn: async ({ pageParam }) => {
      const [cursor, previousPageSearchId] = pageParam.split(':');

      /** see useProductSearch for explanation of search ID logic */
      const activeSearchId = previousPageSearchId || searchId;

      return {
        searchId: activeSearchId,
        ...(await fetchSearchProducts({
          params: buildSearchQueryParams({
            what: searchInput,
            searchFilters,
            country: DEFAULT_LOCATION,
            after: cursor,
            user_id: user?.id,
            includeBoostedListings: true,
          }),
          searchId: activeSearchId,
          sessionId,
        })),
      };
    },
    initialPageParam: `:`, // empty param of the form `{cursor}:{searchId}`
    getNextPageParam: (lastPage) => {
      const { has_more, last } = lastPage.data.page_info ?? {};
      return has_more && last ? `${last}:${lastPage.searchId}` : null;
    },
    select: (data) => ({
      ...data,
      pages: data.pages.map((page) => ({
        ...page,
        data: {
          meta: page.data.meta,
          products: page.data.objects as PresApiSearchProduct[],
        },
      })),
    }),
    staleTime: SEARCH_RESULTS_STALE_TIME,
    refetchOnMount: false,
    enabled: useFallbackQuery,
  });

  if (useFallbackQuery) {
    return {
      ...fallbackSearchResponse,
      isFallbackResponse: true,
    };
  }

  return { ...searchResponse, isFallbackResponse: false };
}
